import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ModelInput } from '@/tools/models/model.store';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { FeatureRuleV1 } from '@seeqdev/api/model/FeatureRuleV1';
import type { ModelRuleInputV1Object } from '@seeqdev/api/model/ModelRuleInputV1Object';
import type { ModelVersionOutputV1 } from '@seeqdev/api/model/ModelVersionOutputV1';

/** Available model types used throughout model tools and scaling tables */
export const MODEL_TYPES = {
  TOOL: 'TOOL',
  SCALING: 'SCALING',
} as const;

/** Version and draft metadata enriched onto model list items for Enterprise users with Locking. */
export type ModelVersionMetadata = {
  /** Whether a draft exists for the current user */
  hasDraft: boolean;
  /** The versionId of the current user's draft, if one exists */
  draftVersionId?: string;
  /** The production version number (e.g. 1, 2, 3) or undefined if no versions exist */
  productionVersionNumber?: number;
  /** Total number of published versions */
  versionCount: number;
  /** Whether the production version is locked (true if any versions exist, since publishing locks the draft) */
  isLocked: boolean;
  /** Published versions, sorted newest first */
  versions: ModelVersionOutputV1[];
};

/** Union type of supported model type values from `MODEL_TYPES`. */
export type ModelType = (typeof MODEL_TYPES)[keyof typeof MODEL_TYPES];

/** Shape for identifying a model input by any of its known ID fields. */
export type ModelInputIds = { inputFeatureId?: string; referenceId?: string; id?: string };

/** Value object from the output select UI for adding outputs to a model. */
export type ModelOutputSelectValue = {
  id: string;
  name: string;
};

/** Formula parameter entry returned by the formulas API. */
type FormulaParameter = {
  name?: string;
  item?: {
    id?: string;
  };
};

/** Subset of formula metadata used to build formula creator rules for derived features. */
export type FormulaOutput = {
  name?: string;
  scopedTo?: string;
  formula?: string;
  parameters?: FormulaParameter[];
};

/** Dependency item returned from `getDependencies` for model outputs.
 * Used to build tool inputs and track parent relationships for dependency filtering.
 */
export type OutputDependencyItem = {
  id: string;
  name: string;
  type: string;
  ancestors?: Array<{ name?: string }>;
  parameterOf?: Array<{ id: string }>;
};

/** Newly added derived feature created in the UI (unsaved until model save). */
export type NewlyAddedDerivedFeature = {
  id?: string;
  /** Provided if the feature is an intermediate feature */
  referenceId?: string;
  isOutput: boolean;
  name: string;
  rules?: FeatureRuleV1[];
  selectedItem?: { name: string; id: string };
  /** True if this intermediate is being treated as an input */
  intermediateAsInput?: boolean;
  /** True if this was originally an existing intermediate */
  wasExistingIntermediate?: boolean;
  /** Preserve original feature for restore */
  originalFeature?: DerivedFeatureV1;
};

/**
 * Output that can be added to the model. Requires id for dependency resolution.
 */
export type SelectableOutput = NewlyAddedDerivedFeature & { id: string };

/** Pairs a derived feature with its original index before sorting, used for dependency-based reordering. */
export type IndexedFeature = {
  feature: NewlyAddedDerivedFeature;
  oldIndex: number;
};

/** Newly added model input created in the UI (unsaved until model save). */
export type NewlyAddedInputFeature = ModelInput & {
  referenceId: string;
  id: string;
};

/** Extended derived feature type used in model tools to include selected item info for edited formula inputs. */
export type ModelDerivedFeature = DerivedFeatureV1 & {
  selectedItem?: { name: string; id: string };
};

/**
 * Internal type that extends ModelRuleInputV1Object with a featureId field for reindexing.
 * The featureId stores the id of the referenced feature, allowing reliable
 * matching after features are reordered by sorting/filtering. The featureId is stripped
 * before the final save to the backend.
 */
export type ModelRuleInputWithFeatureId = ModelRuleInputV1Object & {
  featureId?: string;
};

/** Extended derived feature type used in model tools that includes an optional column definition id for derived
 * features to map column names correctly.
 */
export type DerivedFeatureWithColumnDefinition = DerivedFeatureV1 & {
  columnDefinition?: Pick<ColumnDefinition, 'id'>;
};
